// Web Audio engine using REAL recorded tabla bol + tanpura samples.
// - Tabla bols: from the open-source Naad project (MIT licensed).
// - Tanpura per-key recordings: long Sa-Pa-Sa loops from kksongs.org.

let _ctx = null;
let _master = null;
const _bufferCache = new Map();
const _bufferLoads = new Map(); // url -> Promise<AudioBuffer>

const TABLA_FILES = {
    dha: "/audio/tabla/dha.wav",
    dhin: "/audio/tabla/dhin.wav",
    dhit: "/audio/tabla/dhit.wav",
    dhun: "/audio/tabla/dhun.wav",
    ga: "/audio/tabla/ga.wav",
    ge: "/audio/tabla/ge.wav",
    ke: "/audio/tabla/ke.wav",
    na: "/audio/tabla/na.wav",
    ta: "/audio/tabla/ta.wav",
    ti: "/audio/tabla/ti.wav",
    tin: "/audio/tabla/tin.wav",
    tun: "/audio/tabla/tun.wav",
};

// Real per-key tanpura recordings (Sa-Pa-Sa tuning) sourced from
// kksongs.org. One MP3 per key — no pitch shifting required.
const TANPURA_FILES = {
    C: "/audio/tanpura/tanpura_C.mp3",
    "C#": "/audio/tanpura/tanpura_Csharp.mp3",
    D: "/audio/tanpura/tanpura_D.mp3",
    "D#": "/audio/tanpura/tanpura_Dsharp.mp3",
    E: "/audio/tanpura/tanpura_E.mp3",
    F: "/audio/tanpura/tanpura_F.mp3",
    "F#": "/audio/tanpura/tanpura_Fsharp.mp3",
    G: "/audio/tanpura/tanpura_G.mp3",
    "G#": "/audio/tanpura/tanpura_Gsharp.mp3",
    A: "/audio/tanpura/tanpura_A.mp3",
    "A#": "/audio/tanpura/tanpura_Asharp.mp3",
    B: "/audio/tanpura/tanpura_B.mp3",
};

export function getCtx() {
    if (!_ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        _ctx = new AC();
        _master = _ctx.createGain();
        _master.gain.value = 0.85;
        _master.connect(_ctx.destination);
    }
    if (_ctx.state === "suspended") _ctx.resume();
    return _ctx;
}

export function masterGain() {
    getCtx();
    return _master;
}

export function setMasterVolume(v) {
    if (!_master) getCtx();
    _master.gain.setTargetAtTime(v, _ctx.currentTime, 0.02);
}

async function loadBuffer(url) {
    if (_bufferCache.has(url)) return _bufferCache.get(url);
    if (_bufferLoads.has(url)) return _bufferLoads.get(url);
    const ctx = getCtx();
    const p = fetch(url)
        .then((r) => {
            if (!r.ok) throw new Error(`Failed to load ${url}: ${r.status}`);
            return r.arrayBuffer();
        })
        .then((ab) => ctx.decodeAudioData(ab))
        .then((buf) => {
            _bufferCache.set(url, buf);
            _bufferLoads.delete(url);
            return buf;
        })
        .catch((e) => {
            _bufferLoads.delete(url);
            throw e;
        });
    _bufferLoads.set(url, p);
    return p;
}

function playBuffer(buffer, when, { gain = 1, loop = false, playbackRate = 1 } = {}) {
    getCtx();
    const src = _ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = loop;
    src.playbackRate.value = playbackRate;
    const g = _ctx.createGain();
    g.gain.value = gain;
    src.connect(g).connect(_master);
    src.start(when);
    // attach gain node so callers can fade out
    src._gainNode = g;
    return src;
}

export function stopSource(src) {
    if (!src) return;
    try {
        const ctx = getCtx();
        const now = ctx.currentTime;
        // 80ms fade-out to avoid clicks
        if (src._gainNode) {
            src._gainNode.gain.cancelScheduledValues(now);
            src._gainNode.gain.setValueAtTime(src._gainNode.gain.value, now);
            src._gainNode.gain.linearRampToValueAtTime(0.0001, now + 0.08);
        }
        src.stop(now + 0.1);
    } catch (e) {
        /* already stopped */
    }
}

// ───────────────────────────────────────────────────────────────
// TANPURA — real per-key recording
// ───────────────────────────────────────────────────────────────
export function preloadTanpura(noteName = "G") {
    const url = TANPURA_FILES[noteName] || TANPURA_FILES.G;
    return loadBuffer(url);
}

export async function startTanpura(noteName, gain = 0.9) {
    const ctx = getCtx();
    const url = TANPURA_FILES[noteName] || TANPURA_FILES.G;
    const buffer = await loadBuffer(url);
    return playBuffer(buffer, ctx.currentTime + 0.05, { gain, loop: true });
}

// ───────────────────────────────────────────────────────────────
// TABLA BOLS — map a written bol to one or more real samples
// ───────────────────────────────────────────────────────────────
function bolToFile(bol) {
    const b = (bol || "").trim().toLowerCase();
    if (!b || b === "—" || b === "-" || b === "s") return null;
    // Compound bols → split into a quick sequence
    if (b.startsWith("trkt") || b.startsWith("tirkit") || b === "tirakita")
        return ["ti", "na", "ke", "ta"];
    if (b === "kita") return ["ke", "ta"];
    if (b === "tita") return ["ti", "ta"];
    if (b === "kata") return ["ke", "ta"];
    if (b === "gadi") return ["ga", "dhin"];
    if (b === "gana") return ["ga", "na"];
    if (b === "dhage") return ["dha", "ge"];

    // Direct mappings
    if (b.startsWith("dha")) return "dha";
    if (b.startsWith("dhin") || b.startsWith("dhi")) return "dhin";
    if (b.startsWith("dhit")) return "dhit";
    if (b.startsWith("dhun")) return "dhun";
    if (b.startsWith("tin")) return "tin";
    if (b.startsWith("tun") || b === "tu") return "tun";
    if (b.startsWith("ti")) return "ti";
    if (b.startsWith("ta") || b === "kat") return "ta";
    if (b.startsWith("na")) return "na";
    if (b.startsWith("ge") || b.startsWith("ghe") || b.startsWith("gha"))
        return "ge";
    if (b.startsWith("ga")) return "ga";
    if (b.startsWith("ke") || b.startsWith("ka") || b.startsWith("ki"))
        return "ke";
    return "ta";
}

export function preloadTablaSamples() {
    return Promise.all(Object.values(TABLA_FILES).map(loadBuffer));
}

export function playBol(bol, when, accent = false, semitones = 0) {
    const ctx = getCtx();
    const v = accent ? 1.15 : 0.85;
    const mapping = bolToFile(bol);
    if (!mapping) return;
    const list = Array.isArray(mapping) ? mapping : [mapping];
    const rate = Math.pow(2, semitones / 12);
    list.forEach((name, i) => {
        const url = TABLA_FILES[name];
        if (!url) return;
        const buf = _bufferCache.get(url);
        const playAt = when + i * 0.06;
        if (!buf) {
            loadBuffer(url).then((b) => {
                playBuffer(b, Math.max(ctx.currentTime, playAt), {
                    gain: v / Math.sqrt(list.length),
                    playbackRate: rate,
                });
            });
            return;
        }
        playBuffer(buf, playAt, { gain: v / Math.sqrt(list.length), playbackRate: rate });
    });
}

// ───────────────────────────────────────────────────────────────
// METRONOME — synthetic click (keep tight, no sample dependency)
// ───────────────────────────────────────────────────────────────
export function clickMetronome(time, accent = false) {
    const ctx = getCtx();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = accent ? 1500 : 900;
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(accent ? 0.5 : 0.32, time + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0005, time + 0.06);
    osc.connect(g).connect(_master);
    osc.start(time);
    osc.stop(time + 0.08);
}
